import { useEffect, useRef } from "react";
import { FragmentsManager } from "@thatopen/components";
import { Viewer } from "@treegram/viewer-core";

import useViewerContext from "@/services/context/viewer/instance/use-viewer-context.ts";
import useViewerItemsVisibilityContext from "@/services/context/viewer/items-visibility/use-viewer-items-visibility-context.ts";
import { resolveViewerObjectIds } from "@/utils/viewer-object-identity.ts";

const useViewerItemsVisibility = (): void => {
    const { viewer, isLoading } = useViewerContext();
    const { visibleItems } = useViewerItemsVisibilityContext();
    const previous = useRef<{ viewer: Viewer; ids: string[] }>();
    const pending = useRef<Promise<void>>(Promise.resolve());

    useEffect(() => {
        if (!viewer || isLoading) {
            return undefined;
        }

        let cancelled = false;

        const synchronizeVisibility = async () => {
            if (cancelled) {
                return;
            }

            const fragments = viewer.getComponents().get(FragmentsManager);
            const aliases = await resolveViewerObjectIds(visibleItems, fragments.list.values());

            if (cancelled) {
                return;
            }

            const ids = Array.from(new Set(Array.from(aliases.values()).flat()));
            const visibleIds = new Set(ids);
            const previousIds = previous.current?.viewer === viewer ? previous.current.ids : [];
            const hiddenIds = previousIds.filter(id => !visibleIds.has(id));

            if (hiddenIds.length > 0) {
                await viewer.hideItems(hiddenIds);
            }

            // Reapply visibility after model replacement: Boomer loaders start hidden.
            await viewer.showItems(ids);
            previous.current = { viewer, ids };
        };

        // Keep rapid checkbox changes in order while resolving model GUIDs asynchronously.
        pending.current = pending.current.then(synchronizeVisibility).catch((error: unknown) => {
            console.error("Erreur pendant la mise à jour de la visibilité des modèles", error);
        });

        return () => {
            cancelled = true;
        };
    }, [viewer, isLoading, visibleItems]);
};

export default useViewerItemsVisibility;
